import React, { useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useAsyncFn } from 'react-use';

import beRefundPayment from 'actions/payment/beRefundPayment.action';
import determineErrorMessage from 'bl/response/determineErrorMessage.bl';
import Button from 'components/form/Button.component';
import Icon from 'components/generic/Icon.component';
import InlineSpinner from 'components/generic/InlineSpinner.component';
import CurrencySign from 'components/price/CurrencySign.component';
import { useTranslation } from 'providers/GILT.provider';
import call from 'utils/functions/call.util';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import logger from 'utils/logger.util';

import CN from './DepositRefundItem.module.scss';

const L = logger('DepositRefundItem');

const STATUS = Object.freeze({
  loading: 'loading',
  done: 'done',
  ready: 'ready',
  error: 'error',
});

const DepositRefundItem = ({ amount, intentId, onRefund, onError = alwaysNoop, beRefundPayment }) => {
  const [status, setStatus] = useState(STATUS.ready);

  const { t } = useTranslation();

  const [{ error, loading, value }, handleRefund] = useAsyncFn(async () => {
    L.debug('()', 'handleRefund()', intentId, amount);
    setStatus(STATUS.loading);

    if (!intentId) {
      L.warn('handleRefund()', 'paymentIntentId:', intentId);
      return null;
    }

    const result = await beRefundPayment({ paymentIntentId: intentId, refundAmount: amount, isDeposit: true });
    const message = determineErrorMessage(result);

    if (result?.is?.ok) {
      setStatus(STATUS.done);
      call('DepositRefundItem.onRefund', onRefund, { status: STATUS.done, intentId, amount });
    } else {
      setStatus(STATUS.ready);
      L.warn('handleRefund()', { message, result });
      call('DepositRefundItem.onError', onError, { status: STATUS.error, intentId, amount, message, result });
    }
  }, [amount, beRefundPayment, intentId, onError, onRefund]);

  // NOTE: using memo here as to only print the message whenever the values are changed, not every component render
  useMemo(() => void L.debug('()', { error, loading, value, intentId }), [error, intentId, loading, value]);

  return (
    <div data-bem="DepositRefundItem" className={CN.component}>
      <div className={CN.text}>{t('balanceSheet.depositRefundWizard.deposit')}</div>
      <div data-bem="DepositRefundItem__amount" className={CN.amount}>
        <CurrencySign value={amount} />
      </div>
      <div data-bem="DepositRefundItem__children" className={CN.children}>
        {STATUS.loading === status && (
          <Button variant="outlineForce">
            <InlineSpinner variant="small" /> {t('balanceSheet.depositRefundWizard.refundingButton')}
          </Button>
        )}

        {STATUS.ready === status && (
          <Button variant="outlineForce" onClick={handleRefund}>
            {t('balanceSheet.depositRefundWizard.refundButton')}
          </Button>
        )}

        {STATUS.done === status && (
          <Button variant="outlineForce">
            <Icon variant="markCircle" />
            {t('balanceSheet.depositRefundWizard.refundedButton')}
          </Button>
        )}
      </div>
    </div>
  );
};

const mapDispatchToProps = { beRefundPayment }; // FIXME: bindActionCreators is missing

const DepositRefundItemHoc = connect(null, mapDispatchToProps)(DepositRefundItem);

// noinspection JSUnusedGlobalSymbols
export default DepositRefundItemHoc;
